/**
 * Role Packages Service
 *
 * CRUD for workspace-managed (custom) role packages.
 * Built-in packages (ROLE_PACKAGES in rolePackages.ts) are not stored in the
 * DB — this service only deals with rows in the role_packages table.
 */

import { getSupabaseClient, isSupabaseConfigured } from './supabaseClient';
import type { RolePackageDefinition } from '../types/prioritization/rolePackages';

// ============================================================================
// Types
// ============================================================================

export interface CustomRolePackage {
  id: string;
  workspaceId: string;
  name: string;
  description: string | null;
  theme: RolePackageDefinition['theme'];
  roleSlugs: string[];
  typicalPersona: string | null;
  createdBy: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateRolePackageInput {
  workspaceId: string;
  name: string;
  description?: string;
  theme?: RolePackageDefinition['theme'];
  roleSlugs: string[];
  typicalPersona?: string;
  createdBy?: string;
}

export interface UpdateRolePackageInput {
  name?: string;
  description?: string;
  theme?: RolePackageDefinition['theme'];
  roleSlugs?: string[];
  typicalPersona?: string;
}

// ============================================================================
// Helpers
// ============================================================================

function rowToPackage(row: Record<string, unknown>): CustomRolePackage {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    name: row.name as string,
    description: (row.description as string | null) ?? null,
    theme: (row.theme as RolePackageDefinition['theme']) ?? 'cross-functional',
    roleSlugs: (row.role_slugs as string[]) ?? [],
    typicalPersona: (row.typical_persona as string | null) ?? null,
    createdBy: (row.created_by as string | null) ?? null,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

// ============================================================================
// Read
// ============================================================================

export async function getCustomRolePackages(
  workspaceId: string,
): Promise<CustomRolePackage[]> {
  if (!isSupabaseConfigured()) return [];
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('role_packages')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('name');
  if (error) throw new Error(error.message);
  return (data ?? []).map(rowToPackage);
}

// ============================================================================
// Create
// ============================================================================

export async function createCustomRolePackage(
  input: CreateRolePackageInput,
): Promise<CustomRolePackage> {
  if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('role_packages')
    .insert({
      workspace_id: input.workspaceId,
      name: input.name.trim(),
      description: input.description?.trim() ?? null,
      theme: input.theme ?? 'cross-functional',
      role_slugs: input.roleSlugs,
      typical_persona: input.typicalPersona?.trim() ?? null,
      created_by: input.createdBy ?? null,
    })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return rowToPackage(data as Record<string, unknown>);
}

// ============================================================================
// Update
// ============================================================================

export async function updateCustomRolePackage(
  id: string,
  input: UpdateRolePackageInput,
): Promise<CustomRolePackage> {
  if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
  const supabase = getSupabaseClient();
  const patch: Record<string, unknown> = {};
  if (input.name !== undefined) patch.name = input.name.trim();
  if (input.description !== undefined) patch.description = input.description.trim() || null;
  if (input.theme !== undefined) patch.theme = input.theme;
  if (input.roleSlugs !== undefined) patch.role_slugs = input.roleSlugs;
  if (input.typicalPersona !== undefined) patch.typical_persona = input.typicalPersona.trim() || null;

  const { data, error } = await supabase
    .from('role_packages')
    .update(patch)
    .eq('id', id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return rowToPackage(data as Record<string, unknown>);
}

// ============================================================================
// Delete
// ============================================================================

export async function deleteCustomRolePackage(id: string): Promise<void> {
  if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
  const supabase = getSupabaseClient();
  const { error } = await supabase.from('role_packages').delete().eq('id', id);
  if (error) throw new Error(error.message);
}
